import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button,
  Popover,
  Space,
  Tag,
  Input,
  Select,
  Empty,
  Spin,
  Tooltip,
  Typography,
  Divider,
  message,
  Modal,
  Form,
} from 'antd';
import { SmileOutlined, PlusOutlined, CopyOutlined, DeleteOutlined, EditOutlined, ReloadOutlined } from '@ant-design/icons';
import { customEmojiAPI } from '../services/api';

const { Text } = Typography;
const { Search } = Input;

// 后端 API 根路径（去掉 /api 后缀），用于拼接缩略图 URL
const API_ROOT = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(/\/api\/?$/, '');

interface CustomEmojiItem {
  id: number;
  custom_emoji_id: string;
  emoji_fallback: string;
  name: string | null;
  category: string | null;
  thumbnail_url: string | null;
  status: number;
  created_at: string;
}

interface CustomEmojiPickerProps {
  /** 目标 TextArea 的 ref 或 Form field name，用于插入 */
  onInsert: (htmlTag: string) => void;
  /** 按钮样式 */
  buttonSize?: 'small' | 'middle' | 'large';
}

const CustomEmojiPicker: React.FC<CustomEmojiPickerProps> = ({
  onInsert,
  buttonSize = 'small',
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emojis, setEmojis] = useState<CustomEmojiItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>();
  const [searchText, setSearchText] = useState('');
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editingEmoji, setEditingEmoji] = useState<CustomEmojiItem | null>(null);
  const [addForm] = Form.useForm();

  const loadEmojis = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { status: 1 };
      if (selectedCategory) params.category = selectedCategory;
      const res = await customEmojiAPI.getAll(params);
      setEmojis(res.data || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await customEmojiAPI.getCategories();
      setCategories(res.data || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadEmojis();
      loadCategories();
    }
  }, [open, loadEmojis, loadCategories]);

  const handleSelect = (emoji: CustomEmojiItem) => {
    const tag = `<tg-emoji emoji-id="${emoji.custom_emoji_id}">${emoji.emoji_fallback || '?'}</tg-emoji>`;
    onInsert(tag);
    message.success(`已插入 ${emoji.name || emoji.emoji_fallback}`);
  };

  const handleCopy = (emoji: CustomEmojiItem) => {
    const tag = `<tg-emoji emoji-id="${emoji.custom_emoji_id}">${emoji.emoji_fallback || '?'}</tg-emoji>`;
    navigator.clipboard.writeText(tag);
    message.success('HTML 标签已复制到剪贴板');
  };

  const handleAdd = async () => {
    try {
      const values = await addForm.validateFields();
      await customEmojiAPI.create({
        customEmojiId: values.customEmojiId.trim(),
        emojiFallback: values.emojiFallback || '',
        name: values.name || null,
        category: values.category || null,
      });
      message.success('添加成功');
      setAddModalVisible(false);
      addForm.resetFields();
      loadEmojis();
      loadCategories();
    } catch (err: any) {
      if (err?.response?.data?.error) {
        message.error(err.response.data.error);
      }
    }
  };

  const handleEdit = async () => {
    if (!editingEmoji) return;
    try {
      const values = await addForm.validateFields();
      await customEmojiAPI.update(editingEmoji.id, {
        name: values.name || null,
        category: values.category || null,
        emojiFallback: values.emojiFallback || '',
      });
      message.success('更新成功');
      setEditingEmoji(null);
      setAddModalVisible(false);
      addForm.resetFields();
      loadEmojis();
      loadCategories();
    } catch (err: any) {
      if (err?.response?.data?.error) {
        message.error(err.response.data.error);
      }
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await customEmojiAPI.delete(id);
      message.success('删除成功');
      loadEmojis();
    } catch {
      message.error('删除失败');
    }
  };

  const openEditModal = (emoji: CustomEmojiItem) => {
    setEditingEmoji(emoji);
    addForm.setFieldsValue({
      customEmojiId: emoji.custom_emoji_id,
      emojiFallback: emoji.emoji_fallback,
      name: emoji.name,
      category: emoji.category,
    });
    setAddModalVisible(true);
  };

  const openAddModal = () => {
    setEditingEmoji(null);
    addForm.resetFields();
    setAddModalVisible(true);
  };

  const filteredEmojis = searchText
    ? emojis.filter(e =>
        (e.name || '').toLowerCase().includes(searchText.toLowerCase()) ||
        (e.emoji_fallback || '').includes(searchText) ||
        e.custom_emoji_id.includes(searchText)
      )
    : emojis;

  const content = (
    <div style={{ width: 380, maxHeight: 420, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* 顶部搜索和筛选 */}
      <div style={{ marginBottom: 8 }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <Search
            placeholder="搜索名称/emoji"
            size="small"
            style={{ width: 180 }}
            allowClear
            onSearch={setSearchText}
            onChange={(e) => { if (!e.target.value) setSearchText(''); }}
          />
          <Select
            placeholder="分类"
            size="small"
            style={{ width: 100 }}
            allowClear
            value={selectedCategory}
            onChange={setSelectedCategory}
          >
            {categories.map(c => (
              <Select.Option key={c} value={c}>{c}</Select.Option>
            ))}
          </Select>
          <Tooltip title="刷新缩略图">
            <Button size="small" icon={<ReloadOutlined />} onClick={async () => {
              try {
                await customEmojiAPI.refreshThumbnails();
                message.success('缩略图刷新完成');
                loadEmojis();
              } catch {
                message.error('刷新失败');
              }
            }} />
          </Tooltip>
          <Tooltip title="手动添加 Emoji">
            <Button size="small" icon={<PlusOutlined />} onClick={openAddModal} />
          </Tooltip>
        </Space>
      </div>

      <Divider style={{ margin: '4px 0 8px' }} />

      {/* Emoji 网格 */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 200 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : filteredEmojis.length === 0 ? (
          <Empty
            description={
              <span>
                还没有自定义 Emoji
                <br />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  在 Telegram 中发送动态 Emoji 给机器人，机器人会自动采集
                </Text>
              </span>
            }
            imageStyle={{ height: 48 }}
          />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {filteredEmojis.map(emoji => (
              <Tooltip
                key={emoji.id}
                title={
                  <div>
                    <div>{emoji.name || '(未命名)'}</div>
                    <div style={{ fontSize: 11, opacity: 0.8 }}>ID: {emoji.custom_emoji_id}</div>
                    {emoji.category && <div style={{ fontSize: 11, opacity: 0.8 }}>分类: {emoji.category}</div>}
                    <div style={{ fontSize: 11, marginTop: 4 }}>
                      点击插入 | 右键管理
                    </div>
                  </div>
                }
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 24,
                    cursor: 'pointer',
                    borderRadius: 6,
                    border: '1px solid #f0f0f0',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}
                  onClick={() => handleSelect(emoji)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    openEditModal(emoji);
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = '#e6f4ff';
                    (e.currentTarget as HTMLDivElement).style.borderColor = '#91caff';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLDivElement).style.background = '';
                    (e.currentTarget as HTMLDivElement).style.borderColor = '#f0f0f0';
                  }}
                >
                  {emoji.thumbnail_url ? (
                    <img
                      src={`${API_ROOT}${emoji.thumbnail_url}`}
                      alt={emoji.emoji_fallback || '?'}
                      style={{ width: 32, height: 32, objectFit: 'contain' }}
                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling && ((e.target as HTMLImageElement).nextElementSibling as HTMLElement).style.removeProperty('display'); }}
                    />
                  ) : null}
                  <span style={emoji.thumbnail_url ? { display: 'none' } : undefined}>{emoji.emoji_fallback || '?'}</span>
                  {emoji.name && (
                    <span style={{
                      position: 'absolute',
                      bottom: -1,
                      left: 0,
                      right: 0,
                      fontSize: 8,
                      textAlign: 'center',
                      color: '#999',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: '12px',
                    }}>
                      {emoji.name}
                    </span>
                  )}
                </div>
              </Tooltip>
            ))}
          </div>
        )}
      </div>

      <Divider style={{ margin: '8px 0 4px' }} />
      <Text type="secondary" style={{ fontSize: 11 }}>
        点击 Emoji 插入到文本 | 右键编辑/删除 | 在 TG 给机器人发动态 Emoji 自动采集
      </Text>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        title="动态 Emoji 库"
        trigger="click"
        open={open}
        onOpenChange={setOpen}
        placement="bottomLeft"
      >
        <Tooltip title="插入动态 Emoji（Premium）">
          <Button size={buttonSize} icon={<SmileOutlined />}>
            动态 Emoji
          </Button>
        </Tooltip>
      </Popover>

      {/* 添加/编辑 Modal */}
      <Modal
        title={editingEmoji ? '编辑 Emoji' : '手动添加动态 Emoji'}
        open={addModalVisible}
        onOk={editingEmoji ? handleEdit : handleAdd}
        onCancel={() => { setAddModalVisible(false); setEditingEmoji(null); addForm.resetFields(); }}
        width={460}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical">
          <Form.Item
            label="Custom Emoji ID"
            name="customEmojiId"
            rules={[{ required: true, message: '请输入 custom_emoji_id' }]}
            extra="从 Telegram Bot API getUpdates 中查看 entities.custom_emoji_id"
          >
            <Input placeholder="5368324170671202286" disabled={!!editingEmoji} />
          </Form.Item>
          <Form.Item
            label="降级显示 Emoji"
            name="emojiFallback"
            extra="当用户客户端不支持时显示的普通 Emoji"
          >
            <Input placeholder="如: (smile)" style={{ width: 120 }} />
          </Form.Item>
          <Form.Item label="名称/备注" name="name">
            <Input placeholder="如: 火焰特效、闪亮星星" />
          </Form.Item>
          <Form.Item label="分类" name="category">
            <Input placeholder="如: 常用、表情、装饰" />
          </Form.Item>
          {editingEmoji && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                handleDelete(editingEmoji.id);
                setAddModalVisible(false);
                setEditingEmoji(null);
                addForm.resetFields();
              }}
            >
              删除此 Emoji
            </Button>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CustomEmojiPicker;
